import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import { baixarCsv, lerCsv } from "../utils/exportar";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Icon from "../components/ui/Icon";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";

const VAZIO = {
  nome: "", cpf: "", rg: "", data_nascimento: "", cargo: "", equipe: "", subsetor: "", setor: "",
  telefone: "", email: "", endereco: "",
  pis: "", data_admissao: "", matricula: "", posto_ids: [], empresa_id: "", gestor_id: "",
  escala_referencia_data: "", horario_entrada: "", horario_saida: "", almoco_inicio: "", almoco_fim: "",
  horario_sabado_entrada: "", horario_sabado_saida: "", almoco_sabado_inicio: "", almoco_sabado_fim: "",
  // bate_intervalo e modo_ponto "seletor" já vinham true/padrão; exige_autenticacao
  // passou a default true também (pedido do usuário 2026-10-15: "exige
  // confirmação de identidade ao bater ponto" marcado por padrão em cadastro novo).
  bate_intervalo: true, bater_sequencial: true, modo_ponto: "seletor", exige_foto: false,
  pode_trocar_senha: false, exige_autenticacao: true, exige_senha_ponto: false, senha_ponto: "",
};

const SENHA_PADRAO = "123456";

// Sugestão de login pro funcionário novo — nome.sobrenome@dominio-da-empresa.com.br,
// com senha padrão pré-preenchida. Tudo pode ser editado antes de salvar.
function normalizarParteEmail(txt) {
  return (txt || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}
function sugerirEmailLogin(nomeCompleto, empresaNome) {
  const partes = (nomeCompleto || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "";
  const primeiro = normalizarParteEmail(partes[0]);
  const ultimo = partes.length > 1 ? normalizarParteEmail(partes[partes.length - 1]) : "";
  const usuario = [primeiro, ultimo].filter(Boolean).join(".");
  const dominio = normalizarParteEmail(empresaNome) || "suaempresa";
  return usuario ? `${usuario}@${dominio}.com.br` : "";
}

const DIAS_SEMANA = [
  { valor: "dom", label: "Dom" },
  { valor: "seg", label: "Seg" },
  { valor: "ter", label: "Ter" },
  { valor: "qua", label: "Qua" },
  { valor: "qui", label: "Qui" },
  { valor: "sex", label: "Sex" },
  { valor: "sab", label: "Sáb" },
];

// Escala_tipo 'seg_sex'/'seg_sab' são valores legados (continuam existindo
// pra quem já está cadastrado assim, ver diaEsperado() em pontoCalculo.js) —
// ao abrir um desses pra editar, converte pra dias marcados no seletor visual;
// se a pessoa salvar, migra sozinho pra 'personalizado' + escala_dias.
function diasLegado(escalaTipo) {
  if (escalaTipo === "seg_sex") return ["seg", "ter", "qua", "qui", "sex"];
  if (escalaTipo === "seg_sab") return ["seg", "ter", "qua", "qui", "sex", "sab"];
  return [];
}

const ESCALA_LABEL = { "12x36": "12x36", seg_sex: "Seg a sex", seg_sab: "Seg a sáb", personalizado: "Personalizada" };

function formatarData(iso) {
  if (!iso) return "—";
  const [ano, mes, dia] = String(iso).slice(0, 10).split("-");
  return `${dia}/${mes}/${ano}`;
}

function selectStyle() {
  return { width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text };
}

function labelStyle() {
  return { fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 };
}

export default function Funcionarios() {
  const { token, usuario } = useAuth();
  // Excluir apaga o cadastro de vez (ao contrário de Demitir, que só
  // desativa mantendo o histórico) — pedido do usuário 2026-10-15: só
  // administrador deve ver essa opção, gestor não.
  const souAdmin = usuario?.perfil === "admin_empresa";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [funcionarios, setFuncionarios] = useState([]);
  const [postos, setPostos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtroSetor, setFiltroSetor] = useState("");
  const [filtroAdmissao, setFiltroAdmissao] = useState("");
  const [filtroCargo, setFiltroCargo] = useState("");
  const [filtroTurno, setFiltroTurno] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [modalAberto, setModalAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null); // null = novo
  const [form, setForm] = useState(VAZIO);
  const [temAlmoco, setTemAlmoco] = useState(true);
  const [eh12x36, setEh12x36] = useState(false);
  const [escalaDias, setEscalaDias] = useState([]);
  const [temAlmocoSabado, setTemAlmocoSabado] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginSenha, setLoginSenha] = useState(SENHA_PADRAO);
  const [loginEmailTocado, setLoginEmailTocado] = useState(false);
  const [loginPerfil, setLoginPerfil] = useState("funcionario");
  // Perfil escolhido aguardando confirmação — trocar pra Gestor/Administrador
  // exige um "tem certeza?" antes de aplicar, já que muda o que a pessoa
  // enxerga e consegue fazer no sistema inteiro (não é só um campo de cadastro).
  const [confirmarPerfil, setConfirmarPerfil] = useState(null);
  const [buscaPosto, setBuscaPosto] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  // Preenchido só quando o e-mail conflita com outro funcionário da MESMA
  // empresa — mostra um botão pra abrir o cadastro dele direto (ver
  // backend/lib/emailConflito.js).
  const [conflito, setConflito] = useState(null);
  const fileInputRef = useRef(null);
  const [importando, setImportando] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);
  const [secaoAberta, setSecaoAberta] = useState({ pessoais: true, vinculo: true, escala: true });
  const alternarSecao = (nome) => setSecaoAberta((atual) => ({ ...atual, [nome]: !atual[nome] }));

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [f, p, e, u] = await Promise.all([
        apiFetch("GET", "/api/funcionarios", undefined, token),
        apiFetch("GET", "/api/postos", undefined, token),
        apiFetch("GET", "/api/empresas", undefined, token),
        apiFetch("GET", "/api/usuarios", undefined, token),
      ]);
      setFuncionarios(f);
      setPostos(p);
      setEmpresas(e);
      // Guarda todos (não só ativo+funcionário/gestor) — precisa achar o login
      // de um funcionário mesmo que o usuário dele tenha sido desativado.
      setLogins(u);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  // Sugere o e-mail de login automaticamente enquanto a pessoa preenche nome/
  // empresa num cadastro NOVO — só enquanto ela não tiver editado esse campo
  // na mão (loginEmailTocado), senão ficaria sobrescrevendo o que ela digitou.
  useEffect(() => {
    if (selecionado || loginEmailTocado) return;
    const empresaSelecionada = empresas.find((e) => String(e.id) === String(form.empresa_id));
    setLoginEmail(sugerirEmailLogin(form.nome, empresaSelecionada?.nome));
  }, [form.nome, form.empresa_id, empresas, selecionado, loginEmailTocado]);

  const abrirNovo = () => {
    setSelecionado(null);
    setForm(VAZIO);
    setTemAlmoco(true);
    setEh12x36(false);
    setEscalaDias([]);
    setTemAlmocoSabado(false);
    setLoginEmail("");
    setLoginSenha(SENHA_PADRAO);
    setLoginEmailTocado(false);
    setLoginPerfil("funcionario");
    setConfirmarPerfil(null);
    setBuscaPosto("");
    setErro("");
    setConflito(null);
    setSecaoAberta({ pessoais: true, vinculo: true, escala: true });
    setModalAberto(true);
  };

  const abrirEdicao = (f) => {
    setSelecionado(f);
    const loginAtual = f.usuario_id ? logins.find((u) => String(u.id) === String(f.usuario_id)) : null;
    setLoginEmail(loginAtual?.email || "");
    setLoginSenha("");
    setLoginEmailTocado(true);
    setLoginPerfil(loginAtual?.perfil || "funcionario");
    setConfirmarPerfil(null);
    setForm({
      nome: f.nome || "",
      cpf: f.cpf || "",
      rg: f.rg || "",
      data_nascimento: f.data_nascimento || "",
      cargo: f.cargo || "",
      equipe: f.equipe || "",
      subsetor: f.subsetor || "",
      setor: f.setor || "",
      telefone: f.telefone || "",
      email: f.email || "",
      endereco: f.endereco || "",
      pis: f.pis || "",
      data_admissao: f.data_admissao || "",
      matricula: f.matricula || "",
      posto_ids: f.posto_ids || [],
      empresa_id: f.empresa_id || "",
      gestor_id: f.gestor_id || "",
      escala_referencia_data: f.escala_referencia_data || "",
      horario_entrada: f.horario_entrada || "",
      horario_saida: f.horario_saida || "",
      almoco_inicio: f.almoco_inicio || "",
      almoco_fim: f.almoco_fim || "",
      horario_sabado_entrada: f.horario_sabado_entrada || "",
      horario_sabado_saida: f.horario_sabado_saida || "",
      almoco_sabado_inicio: f.almoco_sabado_inicio || "",
      almoco_sabado_fim: f.almoco_sabado_fim || "",
      bate_intervalo: !!f.bate_intervalo,
      modo_ponto: f.modo_ponto || "seletor",
      bater_sequencial: f.bater_sequencial == null ? true : !!f.bater_sequencial,
      exige_foto: f.exige_foto == null ? true : !!f.exige_foto,
      pode_trocar_senha: f.pode_trocar_senha == null ? true : !!f.pode_trocar_senha,
      exige_autenticacao: !!f.exige_autenticacao,
      exige_senha_ponto: !!f.exige_senha_ponto,
      // Nunca vem preenchida (o servidor não devolve a senha de ponto, nem
      // hasheada) — em branco significa "não mexer", igual a senha de login.
      senha_ponto: "",
    });
    setTemAlmoco(!!(f.almoco_inicio || f.almoco_fim));
    setEh12x36(f.escala_tipo === "12x36");
    setEscalaDias(f.escala_dias ? f.escala_dias.split(",").map((d) => d.trim()).filter(Boolean) : diasLegado(f.escala_tipo));
    setTemAlmocoSabado(!!(f.almoco_sabado_inicio || f.almoco_sabado_fim));
    setBuscaPosto("");
    setErro("");
    setConflito(null);
    setSecaoAberta({ pessoais: false, vinculo: false, escala: false });
    setModalAberto(true);
  };

  // Link vindo de outra tela (ex: aviso de e-mail duplicado em Usuários,
  // apontando pro cadastro de quem já usa aquele e-mail) já chega sabendo
  // qual funcionário abrir — abre uma vez só, quando a lista carrega, sem
  // reabrir sozinho depois se a pessoa fechar ou trocar de funcionário.
  const abriuPorLinkRef = useRef(false);
  useEffect(() => {
    if (abriuPorLinkRef.current || funcionarios.length === 0) return;
    const idAlvo = searchParams.get("funcionario_id");
    if (!idAlvo) return;
    const alvo = funcionarios.find((f) => String(f.id) === idAlvo);
    if (alvo) {
      abriuPorLinkRef.current = true;
      abrirEdicao(alvo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funcionarios, searchParams]);

  const alternarDiaSemana = (dia) => {
    setEscalaDias((atual) => (atual.includes(dia) ? atual.filter((d) => d !== dia) : [...atual, dia]));
  };

  // Trocar de volta pra Funcionário aplica na hora — só sair do padrão exige
  // confirmação (ver Modal mais abaixo).
  const escolherPerfil = (novo) => {
    if (novo === "funcionario") {
      setLoginPerfil("funcionario");
      return;
    }
    setConfirmarPerfil(novo);
  };

  const salvar = async () => {
    setErro("");
    setConflito(null);
    if (!form.nome) {
      setErro("Informe o nome do funcionário");
      return;
    }
    if (eh12x36 && !form.escala_referencia_data) {
      setErro("Informe um dia de trabalho confirmado desse mês pra escala 12x36");
      return;
    }
    if (!selecionado && !loginEmail) {
      setErro("Informe o e-mail de login do funcionário");
      return;
    }
    // Editando um funcionário legado sem login ainda: precisa criar um agora —
    // não existe mais opção de deixar sem, todo mundo tem que ter usuário.
    if (selecionado && !temLoginAtual && (!loginEmail || !loginSenha)) {
      setErro("Informe o e-mail e a senha de login desse funcionário");
      return;
    }
    if (selecionado && temLoginAtual && !loginEmail) {
      setErro("Informe o e-mail de login desse funcionário");
      return;
    }
    if (form.exige_senha_ponto) {
      const temSenhaAtual = !!selecionado?.tem_senha_ponto;
      if (!form.senha_ponto && !temSenhaAtual) {
        setErro("Defina uma senha de ponto (4 a 11 dígitos) pra esse funcionário.");
        return;
      }
      if (form.senha_ponto && !/^\d{4,11}$/.test(form.senha_ponto)) {
        setErro("A senha de ponto precisa ter de 4 a 11 dígitos numéricos.");
        return;
      }
    }
    setSalvando(true);
    try {
      const sabadoAtivo = !eh12x36 && escalaDias.includes("sab");
      // Login: cadastro novo ou funcionário legado sem login ainda mandam
      // login_email/login_senha junto (o backend cria o usuário nessa mesma
      // chamada); quem já tem login é atualizado depois, à parte, via
      // PUT /api/usuarios/:id (só e-mail e, se preenchida, a senha nova).
      const criarLoginJunto = !selecionado || !temLoginAtual;
      const payload = {
        ...form,
        empresa_id: form.empresa_id || null,
        login_email: criarLoginJunto ? loginEmail : undefined,
        login_senha: criarLoginJunto ? loginSenha : undefined,
        login_perfil: criarLoginJunto ? loginPerfil : undefined,
        almoco_inicio: !eh12x36 && temAlmoco ? form.almoco_inicio : "",
        almoco_fim: !eh12x36 && temAlmoco ? form.almoco_fim : "",
        bate_intervalo: !eh12x36 && temAlmoco ? form.bate_intervalo : false,
        escala_tipo: eh12x36 ? "12x36" : "personalizado",
        escala_dias: eh12x36 ? "" : escalaDias.join(","),
        escala_referencia_data: eh12x36 ? form.escala_referencia_data : "",
        horario_sabado_entrada: sabadoAtivo ? form.horario_sabado_entrada : "",
        horario_sabado_saida: sabadoAtivo ? form.horario_sabado_saida : "",
        almoco_sabado_inicio: sabadoAtivo && temAlmocoSabado ? form.almoco_sabado_inicio : "",
        almoco_sabado_fim: sabadoAtivo && temAlmocoSabado ? form.almoco_sabado_fim : "",
      };
      if (selecionado) {
        await apiFetch("PUT", `/api/funcionarios/${selecionado.id}`, payload, token);
        if (temLoginAtual) {
          await apiFetch("PUT", `/api/usuarios/${selecionado.usuario_id}`, {
            email: loginEmail,
            senha: loginSenha || undefined,
            perfil: loginPerfil,
          }, token);
        }
      } else {
        await apiFetch("POST", "/api/funcionarios", payload, token);
      }
      await carregar();
      setModalAberto(false);
    } catch (e) {
      setErro(e.message);
      if (e.data?.mesma_empresa && e.data?.funcionario_id) {
        setConflito({ funcionarioId: e.data.funcionario_id, nome: e.data.nome_conflito });
      }
    } finally {
      setSalvando(false);
    }
  };

  const demitir = async (f) => {
    if (!window.confirm(`Demitir ${f.nome}? O cadastro fica desativado, mas o histórico de ponto continua acessível.`)) return;
    try {
      await apiFetch("PUT", `/api/funcionarios/${f.id}`, { ativo: false }, token);
      await carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const reativar = async (f) => {
    try {
      await apiFetch("PUT", `/api/funcionarios/${f.id}`, { ativo: true }, token);
      await carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const excluir = async (f) => {
    if (!window.confirm(`Excluir ${f.nome} definitivamente? Essa ação não pode ser desfeita.`)) return;
    try {
      await apiFetch("DELETE", `/api/funcionarios/${f.id}`, undefined, token);
      await carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const baixarModelo = () => {
    baixarCsv(
      "modelo-funcionarios.csv",
      ["nome", "cpf", "cargo", "setor", "subsetor", "email", "telefone", "matricula", "data_admissao", "escala_tipo", "posto_nome", "empresa_nome"],
      [["João da Silva", "123.456.789-00", "Vigia", "Portaria Noturna", "Turno A", "joao@empresa.com", "(11) 99999-9999", "1001", "2026-01-15", "seg_sex", "Posto Central", "Empresa Exemplo"]]
    );
  };

  const importarArquivo = async (e) => {
    const arquivo = e.target.files[0];
    e.target.value = "";
    if (!arquivo) return;
    setImportando(true);
    try {
      const texto = await arquivo.text();
      const linhas = lerCsv(texto);
      if (linhas.length === 0) {
        window.alert("Não encontrei nenhuma linha válida nesse CSV.");
        return;
      }
      const data = await apiFetch("POST", "/api/funcionarios/importar", { funcionarios: linhas }, token);
      setResultadoImport(data);
      await carregar();
    } catch (err) {
      window.alert(err.message);
    } finally {
      setImportando(false);
    }
  };

  const setoresDisponiveis = [...new Set(funcionarios.map((f) => f.equipe).filter(Boolean))].sort();

  const funcionariosFiltrados = funcionarios.filter((f) => {
    if (filtroSetor && f.equipe !== filtroSetor) return false;
    if (busca && !f.nome.toLowerCase().includes(busca.toLowerCase())) return false;
    if (filtroAdmissao && String(f.data_admissao || "").slice(0, 10) !== filtroAdmissao) return false;
    if (filtroCargo && !(f.cargo || "").toLowerCase().includes(filtroCargo.toLowerCase())) return false;
    if (filtroTurno && f.escala_tipo !== filtroTurno) return false;
    if (filtroStatus && (filtroStatus === "ativo") !== !!f.ativo) return false;
    return true;
  });
  const filtrosColunaAtivos = Boolean(busca || filtroSetor || filtroAdmissao || filtroCargo || filtroTurno || filtroStatus);
  const limparFiltrosColuna = () => {
    setBusca(""); setFiltroSetor(""); setFiltroAdmissao(""); setFiltroCargo(""); setFiltroTurno(""); setFiltroStatus("");
  };

  // 12x36 não deixa a pessoa escolher os dias — o sistema é quem marca,
  // olhando o dia da semana do "primeiro dia de trabalho desse mês" informado.
  const diaSemanaReferencia = eh12x36 && form.escala_referencia_data
    ? new Date(`${form.escala_referencia_data}T12:00:00`).getDay()
    : null;
  const diasExibidos = eh12x36
    ? (diaSemanaReferencia !== null ? [DIAS_SEMANA[diaSemanaReferencia].valor] : [])
    : escalaDias;

  const temLoginAtual = !!(selecionado && selecionado.usuario_id);
  const resumoPessoais = [form.cpf, form.telefone, form.email].filter(Boolean).join(" · ") || "Não preenchido";
  const resumoVinculo = [
    [form.setor, form.equipe, form.subsetor].filter(Boolean).join(" / "),
    form.posto_ids.length > 0 ? `${form.posto_ids.length} posto(s)` : "sem posto",
    loginEmail || "sem login",
  ].filter(Boolean).join(" · ") || "Não preenchido";
  const resumoEscala = eh12x36
    ? `12x36${form.escala_referencia_data ? ` · a partir de ${formatarData(form.escala_referencia_data)}` : ""}`
    : [
        escalaDias.length > 0 ? DIAS_SEMANA.filter((d) => escalaDias.includes(d.valor)).map((d) => d.label).join(", ") : null,
        form.horario_entrada && form.horario_saida ? `${form.horario_entrada.slice(0, 5)}-${form.horario_saida.slice(0, 5)}` : null,
      ].filter(Boolean).join(" · ") || "Não preenchido";

  if (modalAberto) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
          <button
            onClick={() => setModalAberto(false)}
            aria-label="Voltar"
            style={{
              width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${COLORS.border}`, flexShrink: 0,
              background: COLORS.white, color: COLORS.textMid, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon name="arrowLeft" size={17} />
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: COLORS.text, margin: 0, letterSpacing: "-0.02em" }}>
            {selecionado ? selecionado.nome : "Novo funcionário"}
          </h1>
        </div>

        <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Input label="Nome completo" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" />
            </div>
            {selecionado && <Badge tone={selecionado.ativo ? "success" : "neutral"}>{selecionado.ativo ? "Ativo" : "Inativo"}</Badge>}
          </div>

          <Secao titulo="Dados pessoais" resumo={resumoPessoais} aberta={secaoAberta.pessoais} onToggle={() => alternarSecao("pessoais")}>
            <Row>
              <Input label="CPF" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" />
              <Input label="RG" value={form.rg} onChange={(e) => setForm({ ...form, rg: e.target.value })} placeholder="00.000.000-0" />
              <Input label="Data de nascimento" type="date" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} />
            </Row>
            <Row>
              <Input label="Telefone" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(11) 99999-9999" />
              <Input label="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" />
              <Input label="PIS" value={form.pis} onChange={(e) => setForm({ ...form, pis: e.target.value })} placeholder="000.00000.00-0" />
            </Row>
            <Input label="Endereço residencial" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} placeholder="Rua, número, bairro, cidade" />
          </Secao>

          <Secao titulo="Vínculo e postos" resumo={resumoVinculo} aberta={secaoAberta.vinculo} onToggle={() => alternarSecao("vinculo")}>
            <Row>
              <Input label="Cargo" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} placeholder="Ex: Auxiliar de limpeza" />
              <Input label="Data de admissão" type="date" value={form.data_admissao} onChange={(e) => setForm({ ...form, data_admissao: e.target.value })} />
              <Input label="Matrícula" value={form.matricula} onChange={(e) => setForm({ ...form, matricula: e.target.value })} placeholder="Opcional" />
            </Row>
            <Row>
              <Input label="Grupo" value={form.equipe} onChange={(e) => setForm({ ...form, equipe: e.target.value })} placeholder="Ex: Portaria Noturna" />
              <Input label="Sub Grupo" value={form.subsetor} onChange={(e) => setForm({ ...form, subsetor: e.target.value })} placeholder="Opcional — ex: Turno A" />
              <Input label="Setor" value={form.setor} onChange={(e) => setForm({ ...form, setor: e.target.value })} placeholder="Opcional — ex: Recepção" />
            </Row>

            <div>
              <label style={labelStyle()}>Empresa</label>
              <select value={form.empresa_id} onChange={(e) => setForm({ ...form, empresa_id: e.target.value })} style={selectStyle()}>
                <option value="">Não vinculado</option>
                {empresas.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle()}>Postos de trabalho</label>
              {postos.length === 0 ? (
                <p style={{ fontSize: 12.5, color: COLORS.textLight, margin: 0 }}>Nenhum posto cadastrado ainda.</p>
              ) : (
                <>
                  <Input placeholder="Buscar posto..." value={buscaPosto} onChange={(e) => setBuscaPosto(e.target.value)} style={{ marginBottom: 8 }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 200, overflowY: "auto", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: 8 }}>
                    {postos.filter((p) => p.nome.toLowerCase().includes(buscaPosto.toLowerCase())).map((p) => {
                      const marcado = form.posto_ids.includes(p.id);
                      return (
                        <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 6px", borderRadius: RADIUS.sm, cursor: "pointer", fontSize: 13.5, color: COLORS.text }}>
                          <input
                            type="checkbox"
                            checked={marcado}
                            onChange={() => setForm({
                              ...form,
                              posto_ids: marcado ? form.posto_ids.filter((id) => id !== p.id) : [...form.posto_ids, p.id],
                            })}
                            style={{ width: 16, height: 16 }}
                          />
                          {p.nome}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
                Pode marcar mais de um — a batida é liberada se a pessoa estiver em qualquer um dos postos marcados. Sem nenhum marcado, bate de qualquer lugar sem bloqueio.
              </p>
            </div>

            <div>
              <label style={labelStyle()}>
                {temLoginAtual ? "Login (para bater ponto)" : "Login (criado automaticamente para bater ponto)"}
              </label>
              <Row>
                <Input
                  label="E-mail"
                  value={loginEmail}
                  onChange={(e) => { setLoginEmail(e.target.value); setLoginEmailTocado(true); }}
                  placeholder="nome.sobrenome@empresa.com.br"
                />
                <Input
                  label={temLoginAtual ? "Nova senha (opcional)" : "Senha"}
                  value={loginSenha}
                  onChange={(e) => setLoginSenha(e.target.value)}
                  placeholder={temLoginAtual ? "Deixe em branco pra manter a atual" : ""}
                />
              </Row>
              <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
                {temLoginAtual
                  ? "Todo funcionário tem um usuário próprio pra bater ponto — aqui você só edita o e-mail e, se precisar, define uma senha nova."
                  : `A senha padrão é ${SENHA_PADRAO} — já vem preenchida, assim como o e-mail (sugerido a partir do nome e da empresa), mas os dois podem ser alterados antes de salvar.`}
              </p>

              <div style={{ marginTop: 12 }}>
                <label style={labelStyle()}>Perfil de acesso</label>
                <select value={loginPerfil} onChange={(e) => escolherPerfil(e.target.value)} style={selectStyle()}>
                  <option value="funcionario">Funcionário</option>
                  <option value="gestor">Gestor</option>
                  <option value="admin_empresa">Administrador</option>
                </select>
                <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
                  Todo funcionário novo começa como Funcionário. Mudar pra Gestor ou Administrador dá acesso a dados e telas de toda a equipe — por isso pede confirmação.
                </p>
              </div>
            </div>
          </Secao>

          <Secao titulo="Escala e horários" resumo={resumoEscala} aberta={secaoAberta.escala} onToggle={() => alternarSecao("escala")}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: COLORS.text, cursor: "pointer" }}>
              <input type="checkbox" checked={eh12x36} onChange={(e) => setEh12x36(e.target.checked)} style={{ width: 16, height: 16 }} />
              Escala 12x36 (trabalha um dia sim, um dia não)
            </label>

            {eh12x36 ? (
              <>
                <div>
                  <Input
                    label="Primeiro dia de trabalho desse mês"
                    type="date"
                    value={form.escala_referencia_data}
                    onChange={(e) => setForm({ ...form, escala_referencia_data: e.target.value })}
                  />
                  <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
                    Um dia confirmado de trabalho — o sistema calcula sozinho quais outros dias do mês são de trabalho ou folga
                    (alterna a cada dia) e recalcula automaticamente todo mês, já que o 12x36 muda quais dias da semana caem em
                    trabalho de um mês pro outro.
                  </p>
                </div>
                <div>
                  <label style={labelStyle()}>Dias de trabalho</label>
                  <PillsDias ativos={diasExibidos} interativo={false} onToggle={alternarDiaSemana} />
                  <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
                    Marcado sozinho a partir do dia informado acima — não dá pra editar direto aqui.
                  </p>
                </div>
                <Row>
                  <Input label="Entrada" type="time" value={form.horario_entrada} onChange={(e) => setForm({ ...form, horario_entrada: e.target.value })} />
                  <Input label="Saída" type="time" value={form.horario_saida} onChange={(e) => setForm({ ...form, horario_saida: e.target.value })} />
                </Row>
              </>
            ) : (
              <>
                <div>
                  <label style={labelStyle()}>Dias de trabalho</label>
                  <PillsDias ativos={diasExibidos} interativo onToggle={alternarDiaSemana} />
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={temAlmoco}
                    onChange={(e) => {
                      setTemAlmoco(e.target.checked);
                      if (!e.target.checked) setForm({ ...form, almoco_inicio: "", almoco_fim: "", bate_intervalo: false });
                    }}
                    style={{ width: 16, height: 16 }}
                  />
                  Tem intervalo de almoço
                </label>
                <Row>
                  <Input label="Entrada" type="time" value={form.horario_entrada} onChange={(e) => setForm({ ...form, horario_entrada: e.target.value })} />
                  {temAlmoco && (
                    <>
                      <Input label="Início do almoço" type="time" value={form.almoco_inicio} onChange={(e) => setForm({ ...form, almoco_inicio: e.target.value })} />
                      <Input label="Fim do almoço" type="time" value={form.almoco_fim} onChange={(e) => setForm({ ...form, almoco_fim: e.target.value })} />
                    </>
                  )}
                  <Input label="Saída" type="time" value={form.horario_saida} onChange={(e) => setForm({ ...form, horario_saida: e.target.value })} />
                </Row>
                <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: 0 }}>
                  Esse horário vale pros dias marcados acima, exceto sábado, que tem horário próprio logo abaixo.
                </p>

                {escalaDias.includes("sab") && (
                  <div style={{ border: `1.5px dashed ${COLORS.border}`, borderRadius: RADIUS.md, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                    <label style={{ fontSize: 12.5, fontWeight: 700, color: COLORS.text }}>Horário de sábado</label>
                    <Row>
                      <Input label="Entrada" type="time" value={form.horario_sabado_entrada} onChange={(e) => setForm({ ...form, horario_sabado_entrada: e.target.value })} />
                      <Input label="Saída" type="time" value={form.horario_sabado_saida} onChange={(e) => setForm({ ...form, horario_sabado_saida: e.target.value })} />
                    </Row>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={temAlmocoSabado}
                        onChange={(e) => setTemAlmocoSabado(e.target.checked)}
                        style={{ width: 16, height: 16 }}
                      />
                      Tem intervalo de almoço no sábado
                    </label>
                    {temAlmocoSabado && (
                      <Row>
                        <Input label="Início do almoço" type="time" value={form.almoco_sabado_inicio} onChange={(e) => setForm({ ...form, almoco_sabado_inicio: e.target.value })} />
                        <Input label="Fim do almoço" type="time" value={form.almoco_sabado_fim} onChange={(e) => setForm({ ...form, almoco_sabado_fim: e.target.value })} />
                      </Row>
                    )}
                  </div>
                )}

                {temAlmoco && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.bate_intervalo}
                      onChange={(e) => setForm({ ...form, bate_intervalo: e.target.checked })}
                      style={{ width: 16, height: 16 }}
                    />
                    Precisa bater ponto na saída/volta do intervalo
                  </label>
                )}
              </>
            )}

            <div>
              <label style={labelStyle()}>Tipo de ponto</label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[
                  { valor: "seletor", titulo: "Ponto por escolha", desc: "O funcionário escolhe a cada batida se é entrada, saída ou intervalo, na ordem e quantas vezes precisar." },
                  { valor: "corrido", titulo: "Ponto em sequência", desc: "As batidas seguem uma ordem fixa: entrada, saída/volta do intervalo (se houver) e saída. Não dá para pular etapas." },
                ].map((op) => {
                  const marcado = form.modo_ponto === op.valor;
                  return (
                    <button
                      key={op.valor}
                      type="button"
                      onClick={() => setForm({ ...form, modo_ponto: op.valor })}
                      style={{
                        flex: "1 1 200px", textAlign: "left", cursor: "pointer", padding: "10px 12px", borderRadius: RADIUS.md,
                        border: `1.5px solid ${marcado ? COLORS.primary : COLORS.border}`, background: marcado ? COLORS.primaryBg : COLORS.surface,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: marcado ? COLORS.primary : COLORS.text }}>{op.titulo}</span>
                        {op.valor === "seletor" && <Badge tone="success">Recomendado</Badge>}
                      </div>
                      <div style={{ fontSize: 11.5, color: COLORS.textMid, marginTop: 3 }}>{op.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.exige_foto}
                  onChange={(e) => setForm({ ...form, exige_foto: e.target.checked })}
                  style={{ width: 16, height: 16 }}
                />
                Exige foto (selfie) ao bater ponto?
              </label>
            </div>
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.pode_trocar_senha}
                  onChange={(e) => setForm({ ...form, pode_trocar_senha: e.target.checked })}
                  style={{ width: 16, height: 16 }}
                />
                Funcionário pode trocar a própria senha pelo app?
              </label>
            </div>
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.exige_autenticacao}
                  onChange={(e) => setForm({ ...form, exige_autenticacao: e.target.checked })}
                  style={{ width: 16, height: 16 }}
                />
                Exige confirmação de identidade ao bater ponto?
              </label>
              <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 3, marginLeft: 24 }}>
                Mostra uma última tela com nome, CPF, posto e horário antes de registrar — a pessoa confirma ou cancela.
              </div>
            </div>
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.exige_senha_ponto}
                  onChange={(e) => setForm({ ...form, exige_senha_ponto: e.target.checked })}
                  style={{ width: 16, height: 16 }}
                />
                Exige senha (PIN) pra bater ponto?
              </label>
              <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 3, marginLeft: 24, marginBottom: form.exige_senha_ponto ? 8 : 0 }}>
                Pede esse PIN assim que a pessoa toca pra bater ponto, antes de qualquer outra coisa — útil em aparelho compartilhado entre vários funcionários.
              </div>
              {form.exige_senha_ponto && (
                <div style={{ marginLeft: 24, maxWidth: 220 }}>
                  <Input
                    label={selecionado?.tem_senha_ponto ? "Nova senha de ponto (opcional)" : "Senha de ponto"}
                    value={form.senha_ponto}
                    onChange={(e) => setForm({ ...form, senha_ponto: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                    placeholder={selecionado?.tem_senha_ponto ? "Deixe em branco pra manter a atual" : "4 a 11 dígitos"}
                    inputMode="numeric"
                  />
                </div>
              )}
            </div>
          </Secao>

          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
              {conflito && (
                <>
                  {" "}
                  <button
                    onClick={() => {
                      const alvo = funcionarios.find((f) => f.id === conflito.funcionarioId);
                      if (alvo) abrirEdicao(alvo);
                    }}
                    style={{ background: "none", border: "none", color: COLORS.primary, fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline" }}
                  >
                    Editar cadastro de {conflito.nome}
                  </button>
                </>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
            <Btn onClick={salvar} disabled={salvando} style={{ flex: 1 }}>
              {salvando ? "Salvando..." : selecionado ? "Salvar alterações" : "Cadastrar funcionário"}
            </Btn>
          </div>
        </div>
        </Card>

        <Modal
          open={!!confirmarPerfil}
          onClose={() => setConfirmarPerfil(null)}
          title="Confirmar mudança de perfil"
          width={420}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ fontSize: 13.5, color: COLORS.text, margin: 0 }}>
              Tem certeza que quer mudar o perfil de acesso de <strong>{form.nome || "esse funcionário"}</strong> pra{" "}
              <strong>{confirmarPerfil === "gestor" ? "Gestor" : "Administrador"}</strong>?
            </p>
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>
              A pessoa passa a enxergar e gerenciar dados de toda a equipe, não só os próprios. Isso só é aplicado quando você salvar o cadastro.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="secondary" onClick={() => setConfirmarPerfil(null)} style={{ flex: 1 }}>Cancelar</Btn>
              <Btn onClick={() => { setLoginPerfil(confirmarPerfil); setConfirmarPerfil(null); }} style={{ flex: 1 }}>Confirmar</Btn>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Funcionários
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Cadastro completo: dados pessoais, vínculo com empresa/posto e escala de trabalho.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={importarArquivo} style={{ display: "none" }} />
          <Btn variant="secondary" onClick={baixarModelo} style={{ padding: "8px 14px", fontSize: 12.5 }}>
            Baixar modelo CSV
          </Btn>
          <Btn variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={importando} style={{ padding: "8px 14px", fontSize: 12.5 }}>
            {importando ? "Importando..." : "Importar CSV"}
          </Btn>
          <Btn onClick={abrirNovo}>+ Novo Funcionário</Btn>
        </div>
      </div>

      <Modal open={!!resultadoImport} onClose={() => setResultadoImport(null)} title="Resultado da importação" width={480}>
        {resultadoImport && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ color: COLORS.success, fontSize: 13.5, background: COLORS.successBg, border: "1px solid #BBF7D0", borderRadius: RADIUS.sm, padding: "9px 13px" }}>
              {resultadoImport.criados.length} funcionário(s) importado(s) com sucesso.
            </div>
            {resultadoImport.falhas.length > 0 && (
              <div>
                <p style={{ fontSize: 12.5, fontWeight: 700, color: COLORS.danger, margin: "0 0 8px" }}>
                  {resultadoImport.falhas.length} linha(s) com problema:
                </p>
                <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                  {resultadoImport.falhas.map((f, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: COLORS.textMid, background: COLORS.bg, borderRadius: 6, padding: "6px 10px" }}>
                      Linha {f.linha}{f.nome ? ` (${f.nome})` : ""}: {f.erro}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Btn onClick={() => setResultadoImport(null)} style={{ alignSelf: "flex-start" }}>Fechar</Btn>
          </div>
        )}
      </Modal>

      <BarraResumoFiltro
        total={funcionariosFiltrados.length}
        totalGeral={funcionarios.length}
        unidade="funcionário(s)"
        filtrosAtivos={filtrosColunaAtivos}
        onLimpar={limparFiltrosColuna}
      />

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando padding={24} />
        ) : funcionariosFiltrados.length === 0 ? (
          <EstadoVazio icon="users" texto={filtrosColunaAtivos ? "Nenhum funcionário encontrado." : "Nenhum funcionário cadastrado ainda."} />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1.5px solid ${COLORS.border}` }}>
                  <ThFiltroTexto label="Nome" value={busca} onChange={setBusca} placeholder="Buscar nome..." minWidth={170} />
                  <ThFiltroTexto label="Admissão" type="date" value={filtroAdmissao} onChange={setFiltroAdmissao} minWidth={140} />
                  <ThFiltroTexto label="Cargo" value={filtroCargo} onChange={setFiltroCargo} placeholder="Buscar..." minWidth={130} />
                  <ThFiltroSelect
                    label="Setor" value={filtroSetor} onChange={setFiltroSetor} minWidth={150}
                    opcoes={[{ value: "", label: "Todos" }, ...setoresDisponiveis.map((s) => ({ value: s, label: s }))]}
                  />
                  <ThFiltroSelect
                    label="Turno" value={filtroTurno} onChange={setFiltroTurno} minWidth={140}
                    opcoes={[{ value: "", label: "Todos" }, ...Object.entries(ESCALA_LABEL).map(([value, label]) => ({ value, label }))]}
                  />
                  <ThFiltroSelect
                    label="Status" value={filtroStatus} onChange={setFiltroStatus} minWidth={110}
                    opcoes={[{ value: "", label: "Todos" }, { value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]}
                  />
                  <ThSimples label="Ações" alinhamento="right" />
                </tr>
              </thead>
              <tbody>
                {funcionariosFiltrados.map((f) => (
                  <tr key={f.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{f.nome}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{formatarData(f.data_admissao)}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{f.cargo || "—"}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{f.setor || f.equipe || "—"}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{ESCALA_LABEL[f.escala_tipo] || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <Badge tone={f.ativo ? "success" : "neutral"}>{f.ativo ? "Ativo" : "Inativo"}</Badge>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <BotaoIcone title="Editar cadastro" onClick={() => abrirEdicao(f)}>
                          <Icon name="edit" size={15} />
                        </BotaoIcone>
                        <BotaoIcone title="Ver histórico de ponto" onClick={() => navigate(`/funcionarios/historico?funcionario_id=${f.id}`)}>
                          <Icon name="calendar" size={15} />
                        </BotaoIcone>
                        {f.ativo ? (
                          <BotaoIcone title="Demitir" onClick={() => demitir(f)}>
                            <Icon name="userX" size={15} color={COLORS.warning} />
                          </BotaoIcone>
                        ) : (
                          <BotaoIcone title="Reativar" onClick={() => reativar(f)}>
                            <Icon name="checkCircle" size={15} color={COLORS.success} />
                          </BotaoIcone>
                        )}
                        {souAdmin && (
                          <BotaoIcone title="Excluir" onClick={() => excluir(f)}>
                            <Icon name="trash" size={15} color={COLORS.danger} />
                          </BotaoIcone>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function BotaoIcone({ title, onClick, children }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        width: 30, height: 30, borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: COLORS.white,
        display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

// Pílulas Dom-Sáb do seletor de dias de trabalho — no modo 12x36 aparecem só
// pra leitura (o sistema marca sozinho, ver diaSemanaReferencia), no modo
// personalizado a pessoa clica pra marcar/desmarcar.
function PillsDias({ ativos, interativo, onToggle }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {DIAS_SEMANA.map((d) => {
        const marcado = ativos.includes(d.valor);
        return (
          <button
            key={d.valor}
            type="button"
            disabled={!interativo}
            onClick={() => interativo && onToggle(d.valor)}
            style={{
              padding: "8px 12px", borderRadius: RADIUS.sm, border: `1.5px solid ${marcado ? COLORS.primary : COLORS.border}`,
              background: marcado ? COLORS.primary : COLORS.white, color: marcado ? COLORS.white : COLORS.text,
              fontSize: 12.5, fontWeight: 700, cursor: interativo ? "pointer" : "default",
              opacity: interativo ? 1 : 0.85,
            }}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{React.Children.map(children, (c) => <div style={{ flex: "1 1 200px", maxWidth: 340 }}>{c}</div>)}</div>;
}

// Bloco recolhível do formulário de funcionário — clica no cabeçalho pra
// abrir/fechar. Fechado, mostra um resumo de uma linha (ver os `resumo*` em
// Funcionarios()) pra dar pra escanear o cadastro sem abrir nada.
function Secao({ titulo, resumo, aberta, onToggle, children }) {
  return (
    <div style={{ border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, overflow: "hidden" }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
          padding: "12px 14px", background: COLORS.primaryBg, border: "none", cursor: "pointer", textAlign: "left",
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.primary }}>{titulo}</div>
          {!aberta && resumo && <div style={{ fontSize: 12, color: COLORS.textMid, marginTop: 2 }}>{resumo}</div>}
        </div>
        <span style={{ fontSize: 13, color: COLORS.primary, transform: aberta ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</span>
      </button>
      {aberta && <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>}
    </div>
  );
}
